import React, { useState, useRef, useEffect } from "react";
import { imgUrl, imgSrcSet } from "@/lib/imageUrl";

/**
 * SmartImage — drop-in <img> replacement:
 *  - Responsive srcSet + sizes (WebP + resized via /api/files or Unsplash CDN)
 *  - Lazy by default, eager + fetchPriority=high when `priority`
 *  - Instant LQIP paint via a base64 blurDataURL painted as background-image
 *    behind the transparent <img>. The real image lands on top when loaded.
 *  - Aspect-ratio reservation prevents CLS
 *
 * All existing className / animations / styles are preserved.
 */
export const SmartImage = React.forwardRef(function SmartImage(
  {
    src,
    alt = "",
    width = 800,
    sizes = "(max-width: 768px) 100vw, 33vw",
    priority = false,
    className = "",
    aspectRatio,
    quality = 80,
    widths,
    blurDataURL,
    style,
    ...rest
  },
  ref,
) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth > 0) setLoaded(true);
  }, [src]);

  if (!src) {
    return (
      <div
        className={`bg-[#FDF2F5] ${className}`}
        style={{ aspectRatio, ...style }}
        aria-label={alt}
      />
    );
  }

  const optimizedSrc = imgUrl(src, width, { quality });
  const srcSet = imgSrcSet(src, widths, { quality });

  // Background painted BEFORE the network image lands. If we have an LQIP
  // blurDataURL, it renders instantly (zero-byte, inline). Otherwise the soft
  // pink brand tint fills the box so there's no white flash.
  const bgStyle = blurDataURL
    ? {
        backgroundImage: `url("${blurDataURL}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }
    : { backgroundColor: "#FDF2F5" };

  return (
    <img
      ref={(node) => {
        imgRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      }}
      src={optimizedSrc}
      srcSet={srcSet}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      fetchPriority={priority ? "high" : "auto"}
      onLoad={() => setLoaded(true)}
      className={className}
      style={{
        aspectRatio,
        // Blur/brand background is always painted; once the real image lands
        // it covers the background naturally. Drop the bg after load so the
        // browser can free the reference.
        ...(loaded ? { backgroundImage: "none", backgroundColor: "transparent" } : bgStyle),
        ...style,
      }}
      {...rest}
    />
  );
});
